import React, { useEffect, Suspense, useState } from 'react'
import {
    Switch,
    Route,
    useRouteMatch,
    useHistory,
} from 'react-router-dom'
import { useAppSelector, useAppDispatch, useOrganization, useDetermineOrganization } from 'app/hooks'
import {
    RootState,
    PageType,
} from 'app/types'
import {
    initializeHome, rebuildHistory, setOrganizationVisited,
} from 'actions/appActions'
import {
    HomeBar,
    Spinner,
} from 'components'
import * as fnc from 'helpers/fnc'
import AboutUsView from './AboutUsView'
import OrganizationView from './OrganizationView'
import ProjectView from './ProjectView'
import { PasswordResetView } from './PasswordResetView'
import { ActivateAccountView } from './ActivateAccountView'
import { FavouritesView } from './FavouritesView'
import { PrivacyPolicyView } from './PrivacyPolicyView'
import { DashboardView } from './DashboardView'
import { organizationRoutes, organizationContent } from 'app/transformers'
import { DynamicPage } from 'views/DynamicPage'
import { ModelhomePage } from 'views/ModelhomePage/ModelhomePage'
import { logger } from 'helpers/logger'
import { getHost, getWindowLocation } from 'helpers/config'
import { hubspotTrackPageView } from 'helpers/scripts'
import { DemoView } from './DemoView'
import { BrokerPortalIndex } from 'views/BrokerPortal/BrokerPortalIndex'
import { BrokerPortalPage } from 'views/BrokerPortal/BrokerPortalPage'

// Map of page types to their corresponding components for organization pages
const organizationPageMap = {
    [PageType.Modelhome]: ModelhomePage,
    [PageType.Custom]: DynamicPage,
    [PageType.Info]: DynamicPage,
    [PageType.Blog]: DynamicPage,
    [PageType.Footer]: DynamicPage,
    // Default fallback
    default: DynamicPage
}

/**
 * Get the appropriate page component for an organization page
 * @param page - The page object containing pageType
 * @param organization - The organization data
 * @param pageLink - The page link
 * @returns The rendered page component
 */
function getOrganizationPageComponent(page, organization, pageLink, config) {
    const pageType = (page.pageTypeId != null ? config.pageTypes[page.pageTypeId]?.name : null ) || PageType.Custom
    const PageComponent = organizationPageMap[pageType] || organizationPageMap.default

    // Pass organization prop instead of app
    return <PageComponent 
        organization={organization}
        pageLink={pageLink}
    />
}

interface HomePageProps {
    organizationLink: string,
    loading: boolean,
}
export function HomePage(props: HomePageProps) {
    const { organizationLink, match, loading } = props

    const { url } = useRouteMatch()
    const dispatch = useAppDispatch()
    const initialized = useAppSelector((state: RootState) => state.app.initialized)
    const error = useAppSelector((state: RootState) => state.app.error)
    const organization = useAppSelector((state: RootState) => state.app.organization)
    const links = useAppSelector((state: RootState) => state.app.links)
    const organizationVisited = useAppSelector((state: RootState) => state.app.organizationVisited)
    const config = useAppSelector((state: RootState) => state.app.config)
    const history = useHistory()
    const fullscreen = useAppSelector((state: RootState) => state.app.fullscreen)
    const standaloneApp = useAppSelector((state: RootState) => state.app.standaloneApp)
    const standaloneOrg = useAppSelector((state: RootState) => state.app.standaloneOrg)
    const customDomain = useAppSelector((state: RootState) => state.app.customDomain)
    const editContent = useAppSelector((state: RootState) => state.app.editContent)
    const user = useAppSelector((state: RootState) => state.user.data)
    const determineOrganization = useDetermineOrganization()
    const [test, setTest] = useState(Date.now())

    useEffect(() => {
        const host = getHost()
        const newOrganization = determineOrganization(organizationLink)
        // Validate organization exists
        if (newOrganization && (!organization || newOrganization?.link != organization.link)) {
            dispatch(initializeHome({ organization: newOrganization.link, domain: host, admin: user?.admin }))
        } else {
            // throw new Error("Failed to initialize home")
        }
    }, [])

    useEffect(() => {
        hubspotTrackPageView()
    }, [links])

    useEffect(() => {
        // Whether we've visited an organization root page
        if (organizationVisited) {
            return
        }
        if (links.organizationLink != null && links.appLink == null) {
            dispatch(setOrganizationVisited(true))
        }

    }, [links])


    function getContent() {
        // Determine if using custom domain
        let routes = null
        // let Content = null
        if (organization && !standaloneApp) {
            routes = organizationRoutes(organization)
            // Content = organizationContent(organization)
            /*if (routes) {
                Content = organizationContent(organization)
            }*/
        }

        const prefix = (!customDomain.organization && organizationLink) ? `/${organizationLink}` : ''
        
        return <Suspense fallback={<Spinner />}>
            <Switch>
                {!(standaloneApp && customDomain.app) && <Route path={`${prefix}/search/:appLink?/:pageLink?/:dataLink?/:extraLink?`} render={(props) => {
                    return <ProjectView path={{ baseRoute: PageType.Developments }} {...props} />
                }} />
                }
                {!(standaloneApp && customDomain.app) && <Route path={`${prefix}/developments/:appLink?/:pageLink?/:dataLink?/:extraLink?`} render={(props) => {
                    return <ProjectView path={{ baseRoute: PageType.Developments }} {...props} />
                }} />
                }
                <Route path={`${match.path}/favourites/:appLink?/:pageLink?/:dataLink?`} component={FavouritesView} />
                <Route path={`${match.path}/privacy-policy`} component={PrivacyPolicyView} />
                <Route path={`${match.path}/activate-account/:link`} component={ActivateAccountView} />
                <Route path={`${match.path}/reset-password/:link`} component={PasswordResetView} />
                <Route path={`${match.path}/demo-create/:link`} component={DemoView} />
                <Route path={`${match.path}/dashboard`} component={DashboardView} />
                <Route exact path={`${prefix}/broker-portal`} render={() => {
                    // Custom domain app (e.g. livefruita.com/broker-portal)
                    if (standaloneApp && customDomain.app) {
                        return <BrokerPortalPage appLink={customDomain.app} />
                    }
                    // Standalone org with single app (e.g. /coppercreek/broker-portal)
                    if (standaloneOrg && organization) {
                        const singleApp = config.apps?.find((x) => x.meta.id === organization.apps[0])
                        if (singleApp) {
                            return <BrokerPortalPage appLink={singleApp.meta.link} />
                        }
                    }
                    return <BrokerPortalIndex organizationLink={organizationLink} />
                }} />
                <Route path={`${prefix}/:appLink/broker-portal`} render={(props) => {
                    return <BrokerPortalPage appLink={props.match.params.appLink} />
                }} />
                {routes && routes.map((x) => {
                    return <Route key={x.link} path={`${prefix}/${x.link}`} render={() => {
                        // Use helper to render the correct page component based on pageType
                        return getOrganizationPageComponent(x, organization, x.link, config)
                    }} />
                })}
                {!(standaloneApp && customDomain.app) && <Route path={`${prefix}/:appLink/:pageLink?/:dataLink?/:extraLink?`} render={(props) => {
                    // Removed copy-page routing - functionality moved to InlineContentEditor
                    return <ProjectView organizationLink={organizationLink} {...props} />
                }} />}
                {standaloneApp && customDomain.app && <Route path={`${prefix}/:pageLink?/:dataLink?/:extraLink?`} render={(props) => {
                    // Removed copy-page routing - functionality moved to InlineContentEditor
                    const finalProps = props
                    finalProps.match.params.appLink = customDomain.app
                    return <ProjectView organizationLink={organizationLink} {...finalProps} />
                }} />}
                {(!standaloneApp || standaloneOrg) && <Route path={`${match.path}/`} render={() => {
                    return <OrganizationView />
                }} />
                }
            </Switch>
        </Suspense>
    }

    // Broker portal routes can render with just config loaded (initialized >= 1)
    // They don't need full home initialization
    const currentPath = window.location.pathname
    const isBrokerPortalRoute = currentPath.endsWith('/broker-portal')
    
    if (initialized >= 2 || (initialized >= 1 && isBrokerPortalRoute)) {
        let redirect = null
        if (url.includes('activate-account') || url.includes('reset-password')) {
            redirect = PageType.Home
        }
        return <div id="home-page" className={`page-wrapper fadeIn${fullscreen ? ' fullscreen' : ''}${standaloneApp ? ' standalone' : ''}`}>
            {!loading && <HomeBar redirect={redirect} />}
            {getContent()}
            <div style={{ visibility: 'hidden', position: 'absolute', pointerEvents: 'none' }}>Site Loaded</div>
        </div>
    }
    return null
}
